import React, { useEffect, useState } from 'react';
import { actions, inputs, elements, navigation, overlays } from '@ecosystem/newron-design-system';
import { DataStore } from '../../data/store';
import { BasicTrackingFn, Params } from '../../types';
import { UiDataParams } from '../../data';
import { Modal } from '../../design/modal/Modal';
import { TableCustomiseOption, TableCustomiseOptions } from './types';
import { LeaderColumn } from './data';
import { trackPageElementClicked } from '../../analytics/utils';
import { getCustomiseOptionLabels } from './helper';
import { triggerUpsellQualtrics, UpgradeType } from '../../analytics/upsell.qualtrics';
import { ContextManager } from '../../context/manager';
import { useCorePageState } from '../../page/provider';

type ColumnOption = {
  name: string;
  label: string;
  enrichment?: string;
};

type Props = {
  onClose: () => void;
  customiseOptions: TableCustomiseOptions;
  settings: Params;
  data: DataStore;
  dataColumns: LeaderColumn[];
  updateSettings: (updates: Params) => void;
  trackEvent?: BasicTrackingFn;
};

/**
 * Checks whether the length of an options list is limited to a certain length
 * @param option
 * @returns
 */
const isOptionLimited = (option: TableCustomiseOption) => {
  return (
    option.isMultiSelect &&
    option.selectionLimit > 0 &&
    option.options.length > 0 &&
    option.options.length > option.selectionLimit
  );
};

function CustomiseTable({ onClose, customiseOptions, settings, data, dataColumns, updateSettings, trackEvent }: Props) {
  const [selectedColumns, setSelectedColumns] = useState<string[] | undefined>();
  const [optionSettings, setOptionsSettings] = useState<Params | undefined>();
  const [isUpsellFeatureOrKpisIndicator, setIsUpsellFeatureOrKpisIndicator] = useState<boolean>(false);
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();
  const { Icon } = elements;
  const { Toast } = overlays;
  const { Link } = navigation;
  const { columnsParam, options } = customiseOptions;
  const context = ContextManager.getInstance().get();
  useEffect(() => {
    if (!columnsParam) {
      setSelectedColumns(undefined);
      return;
    }

    let selected = settings[columnsParam] || [];
    if (!selected?.length) {
      selected = dataColumns.map(col => col.column.name);
    }
    setSelectedColumns(selected);
  }, [settings, columnsParam, dataColumns]);

  useEffect(() => {
    if (!options?.length) {
      return;
    }

    const values: Params = {};

    options.forEach(option => {
      // ensure persisted values do not exceed current maximum
      const limit = isOptionLimited(option) ? option.selectionLimit : option.options.length;

      // only pick the persisted 'checked' values that exist in the available options
      values[option.paramName] = getValidOptionValues(option, limit, settings[option.paramName]);

      setIsUpsellFeatureOrKpisIndicator(option.isUpsellFeatureOrKpisIndicator);
    });

    setOptionsSettings(values);
  }, [settings, options]);
  const columnOptions: ColumnOption[] = data
    .getDataColumns()
    .filter(col => col.isFixed !== true)
    .sort((a, b) => {
      if (a.name === 'brand_name') return -1;
      if (b.name === 'brand_name') return 1;
      return 0;
    })
    .map(col => ({
      name: col.name,
      label: col.label,
      enrichment: col.enrichment,
    }));

  columnOptions.push(
    ...data
      .getAvailableColumns()
      .filter(col => col.isFixed !== true)
      .map(col => ({
        name: col.name,
        label: col.label,
        enrichment: col.enrichment,
      }))
  );

  const handleSelect = (name: string) => {
    if (selectedColumns.includes(name)) {
      setSelectedColumns(selectedColumns.filter(v => v !== name));
    } else {
      setSelectedColumns([...selectedColumns, name]);
    }
  };

  const handleClose = (trackingElementName: string) => () => {
    onClose();
    trackPageElementClicked(trackEvent, trackingElementName);
  };

  const handleOptionUpdate = (paramName: string, values: string[]) => {
    const updated = {
      ...{},
      ...optionSettings,
      [paramName]: values,
    };
    setOptionsSettings(updated);
  };

  const handleApply = () => {
    let updates: Params = {};
    const columnsLabels = [];
    if (columnsParam && selectedColumns) {
      const enrichments = new Set<string>();
      const columns: string[] = [];
      selectedColumns.forEach(name => {
        const option = columnOptions.find(opt => opt.name === name);
        if (option) {
          columns.push(option.name);
          columnsLabels.push(option.label);
          option.enrichment && enrichments.add(option.enrichment);
        }
      });
      updates[columnsParam] = columns;
      updates[UiDataParams.uiEnrichments] = Array.from(enrichments);
    }
    if (options && optionSettings) {
      updates = {
        ...updates,
        ...optionSettings,
      };
    }
    const optionLabelsForTracking = getCustomiseOptionLabels(options, optionSettings);
    updateSettings(updates);
    onClose();
    trackPageElementClicked(trackEvent, 'Add and save custom KPIs', {
      [columnsParam]: columnsLabels,
      ...optionLabelsForTracking,
    });
  };

  const showColumnSelection = columnsParam !== undefined && selectedColumns !== undefined;
  const showOptions = options !== undefined && optionSettings !== undefined;

  const { Button } = actions;
  const { Checkbox, CheckboxState } = inputs;
  const corePageState = useCorePageState();

  return (
    <Modal size="md" isOpen onClose={handleClose('Close custom KPIs')}>
      <Modal.Header>Customise table</Modal.Header>
      <Modal.Body>
        <div className="grid grid-cols-2 grid-rows-1">
          {showColumnSelection && (
            <div>
              <div className="font-bold mb-2">Columns</div>
              <div className="pb-6">
                {columnOptions.map(opt => (
                  <div className="py-1" key={opt.name}>
                    <Checkbox
                      onChange={() => handleSelect(opt.name)}
                      label={opt.label}
                      state={selectedColumns.includes(opt.name) ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {showOptions && (
            <div data-test-id="options-section">
              {options.map(option => (
                <OptionSection
                  key={option.paramName}
                  option={option}
                  values={optionSettings[option.paramName]}
                  onChange={handleOptionUpdate}
                />
              ))}
            </div>
          )}
        </div>
        {(data.getUpsellKpisIndicator() || isUpsellFeatureOrKpisIndicator) && (
          <div className="border-t border-light flex p-2">
            <Icon color="default">
              <Icon.icons.LockOpenOutline />
            </Icon>
            <div className="pl-2">
              {`Gain access to more ${isUpsellFeatureOrKpisIndicator ? 'Features or' : ''} KPIs with a subscription`}{' '}
              <Link
                onClick={() => {
                  triggerUpsellQualtrics(
                    context,
                    setToast,
                    corePageState,
                    isUpsellFeatureOrKpisIndicator ? UpgradeType.FEATURE_OR_KPI_UPGRADE : UpgradeType.KPI_UPGRADE
                  );
                }}
              >
                upgrade
              </Link>
              {toast && (
                <Toast type={toast.type} onComplete={() => setToast(undefined)}>
                  {toast.message}
                </Toast>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button mode="secondary" onClick={handleClose('Cancel custom KPIs')}>
          Cancel
        </Button>
        <Button onClick={handleApply}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}

type OptionProps = {
  option: TableCustomiseOption;
  values: string[];
  onChange: (paramName: string, values: string[]) => void;
};
function OptionSection({ option, values, onChange }: OptionProps) {
  const { label, paramName, options, isMultiSelect, selectionLimit } = option;

  const handleRadioChange = (value: string) => {
    onChange(paramName, [value]);
  };

  const handleCheckChange = (value: string) => {
    if (values.includes(value)) {
      onChange(
        paramName,
        values.filter(v => v !== value)
      );
    } else {
      onChange(paramName, [...values, value]);
    }
  };

  const { Checkbox, CheckboxState, RadioButtons } = inputs;

  const maxReached = isOptionLimited(option) && values.length >= selectionLimit;
  const shouldShowLimit = isOptionLimited(option);

  return (
    <div className="">
      <div className="font-bold mb-2">{label}</div>
      <div className="pb-6">
        {!isMultiSelect && <RadioButtons onChange={handleRadioChange} options={options} value={values[0]} />}
        {isMultiSelect && (
          <>
            {shouldShowLimit && (
              <p className="py-1" data-test-id="limit-info">
                Select up to {selectionLimit} items
              </p>
            )}
            {options.length === 0 && <p className="py-1">No items available</p>}
            {options.map(opt => {
              const isChecked = values.includes(opt.value);
              return (
                <div className="py-1" key={opt.value}>
                  <Checkbox
                    label={opt.label}
                    state={isChecked ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                    onChange={() => handleCheckChange(opt.value)}
                    disabled={maxReached && !isChecked}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

const getValidOptionValues = (
  option: TableCustomiseOption,
  limit: number,
  persistedValues: string[] = []
): string[] => {
  const validValues = persistedValues.filter(value => option.options.some(opt => opt.value === value));
  return validValues.slice(0, limit);
};

export { CustomiseTable };
